import { useState, type FormEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button, Card, Checkbox, Notice } from '@ailab/ui';
import {
  checkoutCreatedSchema,
  creditPacksSchema,
  IDEMPOTENCY_KEY_HEADER,
  type checkoutStatusSchema,
} from '@ailab/contracts';
import type { z } from 'zod';
import { apiRequest, errorMessage } from '../../lib/http';
import { useLegalDocuments } from '../../lib/legal';
import styles from './account.module.css';

// Recarga de créditos con Stripe Checkout (RF-02.04, RF-02.07). El precio y los créditos
// los fija el servidor; aquí solo se elige el pack.

const credits = new Intl.NumberFormat('es-ES');
const euros = new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' });

export function formatPrice(amountCents: number, taxIncluded: boolean): string {
  return `${euros.format(amountCents / 100)}${taxIncluded ? '' : ' + IVA'}`;
}

export const CHECKOUT_STATUS_TEXT: Record<z.infer<typeof checkoutStatusSchema>, string> = {
  open: 'Pendiente de pago',
  processing: 'Pago en confirmación',
  credited: 'Créditos añadidos',
  expired: 'Caducada sin pagar',
  failed: 'Pago no completado',
  review: 'En revisión por administración',
};

export function TopUpSection({ cancelled }: { cancelled: boolean }) {
  const packs = useQuery({
    queryKey: ['billing', 'packs'],
    queryFn: () => apiRequest('/me/billing/packs', creditPacksSchema),
    staleTime: 10 * 60 * 1000,
  });
  const terms = useLegalDocuments().data?.items.find((doc) => doc.kind === 'terms');
  const [packId, setPackId] = useState<string>();
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  if (packs.isPending) return null;
  if (packs.isError) {
    return (
      <Card>
        <h2 className={styles.sectionTitle}>Recargar créditos</h2>
        <Notice tone="error">{errorMessage(packs.error)}</Notice>
      </Card>
    );
  }
  if (!packs.data.enabled) {
    return (
      <Card>
        <h2 className={styles.sectionTitle}>Recargar créditos</h2>
        <p>Las recargas todavía no están disponibles en esta versión de AILAB.</p>
      </Card>
    );
  }

  const items = packs.data.items;
  const selected = items.find((pack) => pack.id === packId);

  async function pay(event: FormEvent) {
    event.preventDefault();
    if (!selected) return;
    setBusy(true);
    setError(undefined);
    try {
      const created = await apiRequest('/me/billing/checkouts', checkoutCreatedSchema, {
        method: 'POST',
        body: { packId: selected.id, consent },
        headers: { [IDEMPOTENCY_KEY_HEADER]: crypto.randomUUID() },
      });
      window.location.assign(created.url);
    } catch (failure) {
      setError(errorMessage(failure));
      setBusy(false);
    }
  }

  return (
    <Card>
      <h2 className={styles.sectionTitle}>Recargar créditos</h2>
      {cancelled ? (
        <Notice tone="info">Has cancelado el pago. No se ha cobrado nada.</Notice>
      ) : null}
      <form className={styles.form} onSubmit={(event) => void pay(event)}>
        <fieldset className={styles.packs}>
          <legend className={styles.muted}>Elige un pack</legend>
          {items.map((pack) => (
            <label key={pack.id} className={styles.pack}>
              <input
                type="radio"
                name="pack"
                value={pack.id}
                checked={packId === pack.id}
                onChange={() => setPackId(pack.id)}
              />
              <strong>{credits.format(pack.credits)} créditos</strong>
              <span>{formatPrice(pack.amountCents, pack.taxIncluded)}</span>
            </label>
          ))}
        </fieldset>
        <p className={styles.muted}>
          Stripe calcula el IVA según tu país y te enseña el total antes de confirmar. Los créditos
          se añaden a esta cuenta en cuanto Stripe confirma el pago.
        </p>
        <Checkbox checked={consent} onChange={(event) => setConsent(event.target.checked)}>
          Pido que los créditos se añadan a mi cuenta en cuanto se confirme el pago y acepto que,
          cuando empiece a usarlos, pierdo el derecho de desistimiento sobre los que use
          {terms ? (
            <>
              {' '}
              (
              <a href={terms.path} target="_blank" rel="noopener noreferrer">
                condiciones
              </a>
              )
            </>
          ) : null}
          .
        </Checkbox>
        {error ? <Notice tone="error">{error}</Notice> : null}
        <div>
          <Button type="submit" busy={busy} disabled={!selected || !consent}>
            {selected
              ? `Pagar ${formatPrice(selected.amountCents, selected.taxIncluded)} con Stripe`
              : 'Elige un pack'}
          </Button>
        </div>
      </form>
    </Card>
  );
}
